import React, { useState, Suspense, useEffect } from 'react';
import { ConfigProvider } from 'antd';
import { Routes, Route, useLocation, Navigate } from "react-router";
import ThemeCustomizer from '../components/ThemeCustomizer';
import { AuthProvider, useAuth } from "./AuthProvider";
import { useCommonStore } from '@/store/useCommonStore';
import MainLayout from './MainLayout';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
import locale from 'antd/locale/zh_CN';

// 配置dayjs为中文
dayjs.locale('zh-cn');

// 直接导入页面组件
import Login from "@/pages/login/login";
import Home from "@/pages/home/home";
import CallbackPage from "@/pages/auth/callback";
import BindPage from "@/pages/auth/bind";
import Test from "@/pages/test";
import RustEditor from "@/pages/rust-editor/RustEditor";
import PrivateLayout from "./PrivateLayout";
import GitLab from "@/pages/gitlab";
import FormList from "@/pages/manager/formlist";
import BuildForm from "@/pages/buildform/buildV2";
import MgrMenuList from "@/pages/manager/menulist";
import MgrRoleList from "@/pages/manager/rolelist";
import Popedom from "@/pages/manager/rolepopedom/popedom";
import Userlist from "@/pages/manager/userlist";
import OrderList from "@/pages/cxshop/order/orderlist";
import ProductList from "@/pages/cxshop/product/list";
import ProjectList from "@/pages/project/list";
import ProjectPipeline from "@/pages/project/pipeline";
import ProfileSettings from "@/pages/profile/ProfileSettings";
import BpmnDeploy from "@/pages/bpmn/deploy";
import BpmnDefinitions from "@/pages/bpmn/definitions";
import BpmnInstances from "@/pages/bpmn/instances";
import BpmnTasks from "@/pages/bpmn/tasks";
import ExcalidrawEditorPage from "@/pages/bpmn/excalidraw-editor";
import UserAssignmentSelectorTest from "@/pages/test/UserAssignmentSelectorTest";
import TestPage from "@/pages/home/test";

function AppRouter() {
  const [isSettingVisible, setIsSettingVisible] = useState(false);
  
  // 从全局状态获取主题色
  const primaryColor = useCommonStore(state => state.primaryColor);
  const setPrimaryColor = useCommonStore(state => state.setPrimaryColor);

  // 主题配置
  const theme = {
    token: {
      colorPrimary: primaryColor,
    }
  };

  // 颜色选择器的预设颜色
  const presets = [
    {
      label: '推荐',
      colors: [
        '#791A3E',  // 贯石红（品牌色）
        '#1D6FF0',  // 沉稳蓝
        '#27A37E',  // 优雅绿
        '#ff0000',  // 靛蓝色 - 企业级应用常用色
        '#FF6B6B',  // 暖红色
        '#334155',  // 深空灰
      ],
    },
  ];

  return (
    <ConfigProvider theme={theme} locale={locale}>
      <div>
        {/* 主题定制器 */}
        <ThemeCustomizer
          primaryColor={primaryColor}
          setPrimaryColor={setPrimaryColor}
          isSettingVisible={isSettingVisible}
          setIsSettingVisible={setIsSettingVisible}
        />

        <AuthProvider>
          {/* 由于不再使用懒加载，可以移除Suspense */}
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/auth/callback" element={<CallbackPage />} />
            <Route path="/auth/bind" element={<BindPage />} />
            <Route path="/gitlab" element={<GitLab />} />
            <Route element={<RequireAuth><PrivateLayout /></RequireAuth>}>
              <Route element={<MainLayout />} >
                <Route path="/" element={<Home />} />
                <Route path="/test" element={<Test />} />
                <Route path="/test/user-assignment-selector" element={<UserAssignmentSelectorTest />} />
                <Route path="/home/test" element={<TestPage />} />
                <Route path="/rust-red/editor" element={<RustEditor />} />
                <Route path="/manager/form/list" element={<FormList />} />
                <Route path="/manager/menu/list" element={<MgrMenuList />} />
                <Route path="/manager/role/list" element={<MgrRoleList />} />
                <Route path="/manager/role/popedom" element={<Popedom />} />
                <Route path="/manager/user/list" element={<Userlist />} />
                <Route path="/cxshop/order/list" element={<OrderList />} />
                <Route path="/cxshop/product/list" element={<ProductList />} />
                <Route path="/project/list" element={<ProjectList />} />
                <Route path="/project/pipelines" element={<ProjectPipeline />} />
                <Route path="/profile/settings" element={<ProfileSettings />} />
                <Route path="/bpmn/deploy" element={<BpmnDeploy />} />
                <Route path="/bpmn/definitions" element={<BpmnDefinitions />} />
                <Route path="/bpmn/instances" element={<BpmnInstances />} />
                <Route path="/bpmn/tasks" element={<BpmnTasks />} />
                <Route path="/bpmn/excalidraw-editor/:id?" element={<ExcalidrawEditorPage />} />
              </Route>
              <Route path="/build/:id" element={<BuildForm />} />
              <Route path="/build/V2/:id" element={<BuildForm />} />
            </Route>
          </Routes>
        </AuthProvider>
      </div>
    </ConfigProvider>
  );
}

function RequireAuth({ children }) {
  let auth = useAuth();
  let location = useLocation();
  if (!auth.user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return children;
}

export default AppRouter;